import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * HeroCarousel — accessible multi-slide hero carousel.
 * Matches the existing C4P page design DNA:
 *   - Photo background per slide (backgroundImage prop)
 *   - Dark gradient overlay (from-slate-900/90 via-blue-900/85)
 *   - Animated blur blobs
 *   - Gradient subheadline text
 *   - Glassmorphism bullet card (optional bullets[] prop)
 *   - Full height: min-h-[900px]
 *   - Centered content
 *
 * Accessibility:
 *   - Auto-advance (default 7s), pauses on hover
 *   - Keyboard navigation (ArrowLeft / ArrowRight)
 *   - Touch/swipe support
 *   - aria-live region for screen reader announcements
 *   - Pagination dots with aria roles
 *   - Respects prefers-reduced-motion
 *
 * Slide object props:
 *   backgroundImage  string   Unsplash/URL for hero photo
 *   eyebrow          string   Small badge above headline
 *   headline         string   Main H1 text
 *   subheadline      string   Gradient subheadline
 *   bullets          string[] Optional bullet points (glassmorphism card)
 *   primaryCta       string   Primary button label
 *   primaryCtaHref   string   Primary button link
 *   secondaryCta     string   Secondary button label (optional)
 *   secondaryCtaHref string   Secondary button link
 */
const HeroCarousel = ({ slides = [], autoInterval = 7000 }) => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const touchStartX = useRef(null);

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const goTo = useCallback(
    (index) => {
      const n = ((index % slides.length) + slides.length) % slides.length;
      setCurrent(n);
      setAnnouncement(`Slide ${n + 1} of ${slides.length}: ${slides[n]?.headline}`);
    },
    [slides]
  );

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    if (reducedMotion || paused || slides.length <= 1) return;
    const id = setInterval(() => setCurrent((c) => (c + 1) % slides.length), autoInterval);
    return () => clearInterval(id);
  }, [paused, slides.length, reducedMotion, autoInterval]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [prev, next]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  if (!slides.length) return null;

  return (
    <section
      className="relative overflow-hidden"
      style={{ minHeight: '100svh' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Featured content"
    >
      {/* Screen reader live region */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">{announcement}</div>

      {/* Slides track */}
      <div
        className="flex"
        style={{
          transform: `translateX(-${current * 100}%)`,
          transition: reducedMotion ? 'none' : 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'transform',
        }}
      >
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className="min-w-full"
            role="group"
            aria-roledescription="slide"
            aria-label={`${idx + 1} of ${slides.length}`}
            aria-hidden={idx !== current}
          >
            {/* Slide */}
            <section
              className="relative flex items-start md:items-center overflow-hidden pt-20"
              style={{ minHeight: '100svh' }}
            >
              {/* Background photo */}
              <div className="absolute inset-0 z-0">
                {slide.backgroundImage ? (
                  <img
                    src={slide.backgroundImage}
                    alt=""
                    className="w-full h-full object-cover"
                    aria-hidden="true"
                    loading={idx === 0 ? 'eager' : 'lazy'}
                  />
                ) : (
                  <div className="w-full h-full bg-slate-900" />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-blue-900/85 to-slate-900/75" />
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900/80 to-transparent" />
                <div className="absolute inset-0">
                  <div className="absolute top-40 left-20 w-[400px] h-[400px] bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-3xl" />
                  <div className="absolute bottom-40 right-20 w-[500px] h-[500px] bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl" />
                </div>
              </div>

              <div className="container mx-auto px-4 lg:px-8 relative z-10 py-8 sm:py-12 md:py-16 lg:py-24 pb-20">
                <div className="max-w-5xl mx-auto text-center">
                  {/* Eyebrow badge */}
                  {slide.eyebrow && (
                    <div className="inline-block mb-3 sm:mb-6">
                      <span className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm border border-cyan-400/30 rounded-full text-cyan-200 text-xs sm:text-sm font-bold uppercase tracking-wider">
                        {slide.eyebrow}
                      </span>
                    </div>
                  )}

                  {/* H1 */}
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
                    {slide.headline}
                  </h1>

                  {/* Gradient subheadline */}
                  <h2
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-6 sm:mb-8 md:mb-10 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400"
                    style={{ lineHeight: '1.3' }}
                  >
                    {slide.subheadline}
                  </h2>

                  {/* Optional glassmorphism bullet card */}
                  {slide.bullets && slide.bullets.length > 0 && (
                    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 sm:p-6 md:p-8 border-2 border-white/20 mb-6 sm:mb-8 md:mb-10 shadow-2xl text-left max-w-3xl mx-auto">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {slide.bullets.map((b) => (
                          <div key={b} className="flex items-start gap-3">
                            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-slate-200 text-sm sm:text-base">{b}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTAs */}
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
                    {slide.primaryCta && (
                      <Link
                        to={slide.primaryCtaHref || '/contact'}
                        className="w-full sm:w-auto bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-base sm:text-xl px-8 py-4 sm:px-12 sm:py-7 rounded-xl shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 inline-flex items-center justify-center gap-3"
                        tabIndex={idx !== current ? -1 : 0}
                      >
                        {slide.primaryCta}
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    )}
                    {slide.secondaryCta && (
                      <Link
                        to={slide.secondaryCtaHref || '/contact'}
                        className="w-full sm:w-auto border-2 border-white/30 hover:border-white/60 text-white font-bold text-base sm:text-xl px-8 py-4 sm:px-12 sm:py-7 rounded-xl transition-all duration-300 inline-flex items-center justify-center"
                        tabIndex={idx !== current ? -1 : 0}
                      >
                        {slide.secondaryCta}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        ))}
      </div>

      {/* Prev / Next — hidden on mobile to avoid overlapping content, visible sm+ */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 z-50 items-center"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={next}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-200 border border-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 z-50 items-center"
            aria-label="Next slide"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Pagination dots */}
      {slides.length > 1 && (
        <div
          className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-50"
          role="tablist"
          aria-label="Slide navigation"
        >
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              role="tab"
              aria-selected={idx === current}
              aria-label={`Go to slide ${idx + 1}`}
              className={`h-2 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${
                idx === current ? 'bg-white w-8' : 'bg-white/40 w-2 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export { HeroCarousel };
export default HeroCarousel;
